/*----------------------------------
- TLD PAGE: OTHER TLD PAGES
----------------------------------*/

/**
 * "Other TLD pages" on the kit `Card` (the mock's siblings card, website-tld-insight.html): the other public-set pages as
 * soft pills, "See every TLD" in ink, then the page's own alternates as text links.
 *
 * @docs docs/features/tld-intelligence
 * @rule The other public-set pages, published ones only, so the set stays one crawlable cluster, then the page's own
 *       alternates: its Markdown mirror, its agenda dates and, only while the public API is on (`apiUrl`, D5), its
 *       endpoint. Sibling links carry no tracking: `tld_page_cta_click` names no sibling target, and `hub` means the way
 *       back to the hub.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import Button from '@uniquedomains/components/kit/button/Button';
import Card from '@uniquedomains/components/kit/card/Card';
import TextLink from '@uniquedomains/components/kit/text-link/TextLink';
import { TLD_HUB_PATH, formatTldLabel, tldInsightMarkdownPath } from '@uniquedomains/components/contracts/tlds/public';

import { TLD_PAGE_CONTENT } from '@/client/catalogs/landing/tld/pageContent';
import { track } from '@/client/pages/landing/lib/analytics';

import { buildTldAgendaHref, listTldSiblingLinks } from '../../lib/presentation';

/*----------------------------------
- TYPES
----------------------------------*/

type TldSiblingLinksProps = {
    slug: string;
    /** The rest of the public set, in Product's neutral order. */
    relatedSlugs: readonly string[];
    publishedPaths: ReadonlySet<string> | null;
    apiUrl: string | null;
    onHubClick: () => void;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function TldSiblingLinks({ slug, relatedSlugs, publishedPaths, apiUrl, onHubClick }: TldSiblingLinksProps) {
    const content = TLD_PAGE_CONTENT.insight.siblings;
    const siblings = listTldSiblingLinks(slug, relatedSlugs, publishedPaths);

    return (
        <Card head={{ title: content.heading }} data-testid="tld-page-siblings">
            <div class="tl-sib">
                {siblings.map((sibling) => (
                    <Button key={sibling.slug} variant="soft" href={sibling.href}>{formatTldLabel(sibling.slug)}</Button>
                ))}
                <Button variant="ink" href={TLD_HUB_PATH} onClick={onHubClick} data-testid="tld-page-every-tld">{content.everyTld}</Button>
            </div>
            <div class="tl-sib-links">
                <TextLink href={tldInsightMarkdownPath(slug)} data-testid="tld-page-markdown-link">{content.markdown}</TextLink>
                <TextLink href={buildTldAgendaHref(slug)} data-testid="tld-page-agenda-link">{TLD_PAGE_CONTENT.insight.agendaLink(formatTldLabel(slug))}</TextLink>
                {apiUrl ? (
                    <TextLink href={apiUrl} data-testid="tld-page-api-link" onClick={() => track('tld_page_cta_click', { cta: 'api', tld: slug, href: apiUrl })}>
                        {content.api}
                    </TextLink>
                ) : null}
            </div>
        </Card>
    );
}
